/* eslint-disable no-nested-ternary */
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  formatDate,
  formatQueryError,
} from 'renderer/shared/formatters/formatters';
import { useEffect, useState } from 'react';
import { isDesktop } from 'renderer/shared/constants/environment';
import { useAssignLicenseMutation } from 'renderer/features/products-page/services/products.endpoints';
import { Licenses } from '../schemas/licenses.schemas';

interface LicensesTableProps {
  licenses: Licenses;
  email: string;
}

const tableStyles = {
  tableContainer: {
    // marginTop: '20px',
    marginBottom: '20px',
    border: '1px solid rgba(224, 224, 224, 1)',
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    backgroundColor: '#f5f5f5',
  },
  tableCell: {
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
  },
  tableRow: {
    '&:last-child td': {
      border: 0,
    },
  },
};

const LicensesTable = ({ licenses, email }: LicensesTableProps) => {
  const [openModal, setOpenModal] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [currentSerial, setCurrentSerial] = useState<string | null>(null);

  const [
    assignLicense,
    {
      isLoading: assignLoading,
      isSuccess: assignSuccess,
      error: assignError,
      reset: resetAssign,
    },
  ] = useAssignLicenseMutation();

  useEffect(() => {
    if (assignSuccess) {
      resetAssign();
      setRemoveModalOpen(false);
    }
  }, [assignSuccess, resetAssign]);

  const handleOpenModal = (serial: any) => {
    setCurrentSerial(serial);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <>
      <TableContainer component={Paper} sx={tableStyles.tableContainer}>
        {/* <Table> */}
        <Table sx={tableStyles.table} aria-label="licenses table" size="small">
          <TableHead sx={tableStyles.tableHead}>
            <TableRow>
              <TableCell sx={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>
                NAME
              </TableCell>
              <TableCell sx={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>
                VERSION
              </TableCell>
              <TableCell sx={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>
                HOST APP
              </TableCell>
              <TableCell sx={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>
                SERIAL NUMBER
              </TableCell>
              <TableCell sx={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>
                ACTIVATIONS
              </TableCell>
              <TableCell sx={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>
                STATUS
              </TableCell>
              <TableCell sx={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>
                PURCHASED
              </TableCell>
              <TableCell sx={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>
                ACTIONS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {licenses.map(
              ({ serial, activations, purchased, count, product, active }) => (
                <TableRow sx={tableStyles.tableRow}>
                  <TableCell sx={tableStyles.tableCell}>
                    {product.displayName
                      ? product.displayName
                      : product.description
                      ? product.description
                      : product.name
                      ? product.name
                      : 'Unknown'}
                  </TableCell>
                  <TableCell sx={tableStyles.tableCell}>
                    {product.version}
                  </TableCell>
                  <TableCell sx={tableStyles.tableCell}>
                    {product.host}
                  </TableCell>
                  <TableCell sx={tableStyles.tableCell}>
                    {serial}
                    {isDesktop && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleOpenModal(serial)}
                        sx={{ ml: 1 }}
                      >
                        Manage
                      </Button>
                    )}
                  </TableCell>
                  <TableCell sx={tableStyles.tableCell}>
                    {activations.length} / {count}
                  </TableCell>
                  <TableCell sx={tableStyles.tableCell}>
                    <Chip
                      sx={{
                        color: 'common.white',
                        ml: 1,
                        padding: 0.5,
                        borderRadius: 1,
                        fontWeight: 'bold',
                        height: 'auto',
                        // width: 150,
                        textAlign: 'center',
                        '& .MuiChip-label': {
                          display: 'block',
                          whiteSpace: 'pre-line',
                          // whiteSpace: 'pre-wrap',
                        },
                      }}
                      color={active ? 'success' : 'error'}
                      label={active ? 'Active' : 'Deactivated'}
                    />
                  </TableCell>
                  <TableCell sx={tableStyles.tableCell}>
                    {formatDate(new Date(purchased))}
                  </TableCell>
                  <TableCell sx={tableStyles.tableCell}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => {
                        setCurrentSerial(serial);
                        setRemoveModalOpen(true);
                      }}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={removeModalOpen} onClose={() => setRemoveModalOpen(false)}>
        <DialogTitle>Remove License From Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove license {currentSerial} from this
            account?
          </DialogContentText>
          {assignError && (
            <Alert severity="error">{formatQueryError(assignError)}</Alert>
          )}
          {assignSuccess && (
            <Alert severity="success">Successfully Removed!</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setRemoveModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={assignLoading || assignSuccess}
            onClick={() =>
              assignLicense({
                serial: currentSerial ?? '',
                email,
                removeUser: true,
              })
            }
          >
            Yes, Remove
          </Button>
        </DialogActions>
      </Dialog>
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="manage-serial-title"
        aria-describedby="manage-serial-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            // width: 300,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="manage-serial-title" variant="h6" component="h2">
            Serial: {currentSerial}
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Activate Serial" />
              <Button variant="contained" size="small" onClick={() => {}}>
                Activate
              </Button>
            </ListItem>
            <ListItem>
              <ListItemText primary="Deactivate Serial" />
              <Button variant="contained" size="small" onClick={() => {}}>
                Deactivate
              </Button>
            </ListItem>
          </List>
          <Button variant="outlined" onClick={handleCloseModal} sx={{ mt: 2 }}>
            Close
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default LicensesTable;
